import {
  Box,
  Editable,
  EditableInput,
  EditablePreview,
  Input,
  Link,
} from '@chakra-ui/react';
import { useDndContext } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import type { TabItem } from '../hooks/useTabOrder';
import tabBarStyles from './analytics-view-tabs.module.css';
import styles from './DraggableTabLink.module.css';
import { useJustDraggedRef } from './DraggableTabLinkContext';

type Variant = 'bar' | 'menu';

type DraggableTabLinkProps = {
  tab: TabItem;
  variant: Variant;
  isActive: boolean;
  onNavigate?: () => void;
  testId?: string;
  /** Bar tabs report their DOM element so the active indicator can slide. */
  tabRef?: (el: HTMLAnchorElement | null) => void;
  /** Optional trailing content (e.g. 3-dot menu). */
  trailing?: ReactNode;
  children?: ReactNode;
  /** Menu variant only: render an inline rename input instead of the link. */
  editing?: boolean;
  editableValue?: string;
  onRename?: (val: string) => void;
};

// Block dnd-kit's pointer/mouse sensors from intercepting events inside the
// rename input so native text selection (drag-select, double-click words)
// works as expected.
const stopDragPropagation = {
  onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
  onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
};

export function DraggableTabLink({
  tab,
  variant,
  isActive,
  onNavigate,
  testId,
  tabRef,
  trailing,
  children,
  editing = false,
  editableValue,
  onRename,
}: DraggableTabLinkProps) {
  const { search } = useLocation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id, disabled: editing });

  // Suppress hover-driven UI (highlight bg, 3-dot reveal via _groupHover)
  // while any tab in the bar/menu is being dragged.
  const isAnyDragActive = !!useDndContext().active;
  const justDraggedRef = useJustDraggedRef();

  const handleNavigate = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (justDraggedRef.current === tab.id) {
      event.preventDefault();
      return;
    }
    onNavigate?.();
  };

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    // Source slot stays visible (dimmed) so the label is shown at the drop
    // position the dragged item will snap into.
    opacity: isDragging ? 0.35 : 1,
    touchAction: 'none',
  };

  if (variant === 'bar') {
    return (
      <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        display="inline-flex"
        alignItems="stretch"
      >
        <Link
          ref={(el: HTMLAnchorElement | null) => tabRef?.(el)}
          as={NavLink}
          to={{ pathname: tab.path, search }}
          onClick={handleNavigate}
          data-testid={testId}
          className={classNames(
            tabBarStyles.tabLink,
            tabBarStyles.tabLinkBar,
            isActive && tabBarStyles.tabLinkActive
          )}
        >
          <Box
            as="span"
            className={tabBarStyles.tabLabel}
            data-label={tab.label}
          >
            {children ?? tab.label}
          </Box>
        </Link>
      </Box>
    );
  }

  // Hide the active bg while renaming so the browser's text-selection
  // highlight stays visible against the row.
  const showActiveBg = isActive && !editing;
  const dragProps = editing ? {} : { ...attributes, ...listeners };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...dragProps}
      role={isAnyDragActive ? undefined : 'group'}
      className={classNames(
        styles.menuRow,
        showActiveBg && styles.menuRowActive,
        !isAnyDragActive && !editing && styles.menuRowHoverable
      )}
      display="flex"
      alignItems="center"
    >
      {editing ? (
        <Editable
          defaultValue={editableValue ?? tab.label}
          startWithEditView
          isPreviewFocusable={false}
          onSubmit={onRename}
          submitOnBlur
          flex="1"
          px="14px"
          py="5px"
          fontSize="14px"
          {...stopDragPropagation}
        >
          <EditablePreview width="100%" />
          <Input
            as={EditableInput}
            variant="flushed"
            size="sm"
            fontSize="14px"
          />
        </Editable>
      ) : (
        <Link
          as={NavLink}
          to={{ pathname: tab.path, search }}
          onClick={handleNavigate}
          data-testid={testId}
          className={styles.menuLink}
          display="flex"
          alignItems="center"
          flex="1"
          minWidth={0}
          px="14px"
          py="5px"
          fontSize="14px"
          fontWeight="400"
          _hover={{ textDecoration: 'none' }}
        >
          <Box
            flex="1"
            minWidth={0}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {children ?? tab.label}
          </Box>
        </Link>
      )}
      {trailing}
    </Box>
  );
}
